"use client";
import { useRef, useEffect, ReactNode } from "react";
import { AssistantRuntimeProvider, ChatModelAdapter, useLocalRuntime } from "@assistant-ui/react";
import { getToken } from "@/utils/localstorage";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import axios from "axios";
import { logger } from "@/helpers/tslog";

const apiUrl = process.env.API_URL;
const INIT_SENTINEL = "__INIT_ALPHA_CREATE__"; // visible sentinel to reliably trigger initialize
const OPENING_MESSAGE =
  "Hey! Let’s get started on that share price chart. To begin, tell me the:\n" +
  "1. **Primary company** you want the chart to be on\n" +
  "2. **Time period** for the chart";

export const MyModelAdapter: ChatModelAdapter = {
  async *run({ messages }): AsyncGenerator {
    const authToken = await getToken();

    const lastMessage = messages[messages.length - 1];
    let userInput = "";

    if (lastMessage?.content && Array.isArray(lastMessage.content)) {
      const textPart = lastMessage.content.find(
        (part) => typeof part === "object" && "text" in part && typeof part.text === "string"
      );
      if (textPart) userInput = (textPart as any).text;
    }
    const isInitialization = userInput === INIT_SENTINEL;

    // Debug logging for opening message
    if (isInitialization) {
      console.log("Sending opening message to backend:", OPENING_MESSAGE);
    }

    // Signal run start before the network request to cover backend latency
    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch (e) {}

    const result = await axios.post(
      isInitialization ? `${apiUrl}/alpha-create/initialize` : `${apiUrl}/alpha-create/interact`,
      isInitialization ? { openingMessage: OPENING_MESSAGE } : { userInput },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    // Debug logging for backend response
    if (isInitialization) {
      console.log("Backend response:", result.data.data.response);
    }

    localStorage.setItem("alphaCreateData", JSON.stringify(result.data.data.globalState));
    const { markAnswered, getInitialInputStep, setInitialInputStep } = useAlphaAgentStore.getState();
    const step = 0;
    const globalState = result.data.data.globalState;

    const initialInputNames = ["primaryCompany", "timePeriod", "currency"];

    initialInputNames.forEach((inputName) => {
      const value = globalState[inputName];
      const isFilled =
        value && (typeof value === "string" || (typeof value === "object" && Object.keys(value).length > 0));

      if (isFilled && getInitialInputStep(inputName) !== true) {
        markAnswered(step);
        setInitialInputStep(inputName, true);
      }
    });

    if (result.data.data.step === 2) {
      markAnswered(step);
    }

    try {
      const filtered = (messages || []).filter((m: any) => {
        if (!m || !Array.isArray(m.content)) return true;
        const textPart = m.content.find((p: any) => p && typeof p.text === "string");
        return !(textPart && textPart.text === INIT_SENTINEL);
      });
      localStorage.setItem(
        "messages",
        JSON.stringify([
          ...filtered,
          {
            role: "assistant",
            content: [
              {
                type: "text" as const,
                text: result.data.data.response,
              },
            ],
          },
        ])
      );
    } catch (e) {
      logger.warn("Failed to persist messages", e);
    }

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const fullText = result.data.data.response;
    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch (e) {}
    for (let i = 1; i <= fullText.length; i++) {
      if (i === 1) {
        try {
          window.dispatchEvent(new CustomEvent("ai-first-token"));
        } catch (e) {}
      }
      yield {
        content: [{ type: "text", text: fullText.slice(0, i) }],
      };
      await delay(8);
    }
    try {
      window.dispatchEvent(new CustomEvent("ai-run-end"));
    } catch (e) {}
  },
};

export function MyRuntimeProviderAlphaCreate({ children }: { children: ReactNode }) {
  const { currentStep, isEditInitial } = useAlphaAgentStore();
  return (
    <MyRuntimeProviderReady
      key={`runtime-ready-${isEditInitial}-${currentStep}`}
      isEditInitial={isEditInitial}
      currentStep={currentStep}
    >
      {children}
    </MyRuntimeProviderReady>
  );
}

function MyRuntimeProviderReady({
  children,
  isEditInitial,
  currentStep,
}: {
  children: ReactNode;
  isEditInitial: boolean;
  currentStep: number;
}) {
  let previousMessages: any[] = [];
  try {
    const stored = localStorage.getItem("messages");
    const parsed = stored ? JSON.parse(stored) : [];
    if (Array.isArray(parsed)) previousMessages = parsed;
  } catch (e) {
    logger.warn("Invalid stored messages");
  }

  // Do not clear messages on navigation; preserve history when moving between steps

  const initialMessages = isEditInitial
    ? [
        ...previousMessages,
        {
          role: "assistant",
          content: [
            {
              type: "text" as const,
              text: " Can you please let me know what exactly you'd like to edit?",
            },
          ],
        },
      ]
    : previousMessages;
  const runtime = useLocalRuntime(MyModelAdapter, {
    initialMessages,
  });

  // Trigger initialize after mount without rendering a visible sentinel bubble
  const initSentRef = useRef(false);
  useEffect(() => {
    if (initSentRef.current) return;
    if (isEditInitial) return;
    if (currentStep !== 0) return;

    // Clear old messages cache to ensure fresh opening message
    try {
      localStorage.removeItem("messages");
      console.log("Cleared old messages cache to show fresh opening message");
    } catch (e) {
      console.warn("Failed to clear messages cache", e);
    }

    try {
      const thread = (runtime as any)?.thread;
      if (thread && typeof thread.append === "function") {
        thread.append({
          role: "system",
          content: [{ type: "text", text: INIT_SENTINEL }],
          startRun: true,
        });
        initSentRef.current = true;
      }
    } catch (e) {
      // ignore
    }
  }, [runtime, isEditInitial, currentStep]);

  // No programmatic append required; the sentinel initial message triggers initialize
  return (
    <AssistantRuntimeProvider key={`runtime-${isEditInitial}`} runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}
